import type { ReactNode } from 'react';

/**
 * App-local presentational primitives, layered on top of @mypoz/ui. The shared package holds
 * anything a second app would want; this file holds the MyPoz-specific flavour. Promote things
 * upward when a second app needs them, not before.
 *
 * Components only — class-string helpers live in ../lib/styles.ts so fast refresh keeps working.
 */

export function PageTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-5">
      <h1 className="text-2xl font-semibold text-ink">{title}</h1>
      {subtitle ? <p className="mt-1 text-sm text-muted">{subtitle}</p> : null}
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-sm font-semibold tracking-wide text-muted uppercase">{title}</h2>
      {children}
    </section>
  );
}

/**
 * A form field's label, with a red * when the field must be filled (MYP-129). The * is hidden from
 * screen readers: the field itself carries `required`, which they announce as "required".
 */
export function FieldLabel({
  htmlFor,
  required = false,
  children,
}: {
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm text-muted">
      {children}
      {required ? (
        <span aria-hidden="true" className="ml-0.5 text-danger">
          *
        </span>
      ) : null}
    </label>
  );
}
